import { ChangeEvent, useState } from "react";
import { toast } from "react-toastify";
import { useMutation, useQuery } from "@tanstack/react-query";

import LoadingButton from "@mui/lab/LoadingButton";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";

import { IProfile } from "Types/profile";

import { useAppDispatch } from "Hooks/useRedux";

import { logout } from "Redux/authSlice";

import Api from "Api";

import DatePicker from "Components/DatePicker";

import Header from "./Header";
import Table from "./Table";

interface ITabContentProps {
  activeTab: number;
  index: number;
  loading?: boolean;
  children: any;
}
interface IIinput {
  name: string;
  label: string;
  type: "text" | "number";
}

const Inputs: IIinput[] = [
  { name: "first_name", label: "نام", type: "text" },
  { name: "last_name", label: "نام خانوادگی", type: "text" },
  { name: "national_code", label: "کدملی", type: "number" },
];

function TabContent({ activeTab, index, loading, children }: ITabContentProps) {
  return (
    <Box hidden={activeTab !== index} sx={{ px: 4 }}>
      {loading ? (
        <Stack alignItems="center">
          <CircularProgress />
        </Stack>
      ) : (
        children
      )}
    </Box>
  );
}

export default function Profile() {
  const [tab, setTab] = useState(0);
  const [profileData, setProfileData] = useState<IProfile>({});
  const [userFormData, setUserFormData] = useState<{ [key: string]: any }>({});

  const dispatch = useAppDispatch();

  const handleLogout = () => {
    dispatch(logout());
  };
  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };
  const handleSetData = (event: ChangeEvent<HTMLInputElement>, setter: (data: any) => void) => {
    const { name, value } = event.target;
    setter((prev: { [key: string]: any }) => ({ ...prev, [name]: value }));
  };

  const { data, refetch, isFetching } = useQuery({
    queryKey: ["profile"],
    queryFn: (): Promise<IProfile> =>
      Api.get("/user/profile").then((res) => {
        setProfileData(res.data);
        return res.data;
      }),
  });
  const updateProfileMutation = useMutation({
    mutationFn: () => {
      return Api.put("/user/profile", profileData).then((res) => {
        toast.success("با موفقیت ذخیره شد");
        refetch();
      });
    },
  });
  const addUserMutation = useMutation({
    mutationFn: () => {
      return Api.post("/user/store", userFormData).then((res) => {
        toast.success("کاربر جدید با موفقیت اضافه شد");
      });
    },
  });

  return (
    <>
      <Header>
        <Stack sx={{ flexGrow: 1 }} direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" sx={{ fontWeight: "800" }}>
            حساب کاربری
          </Typography>

          <Stack direction="row" spacing={1}>
            <Button variant="white" size="large" onClick={handleLogout}>
              <LogoutIcon />
            </Button>
          </Stack>
        </Stack>
      </Header>

      <Tabs value={tab} onChange={handleChangeTab} variant="fullWidth" sx={{ mb: 4 }}>
        <Tab label="حساب من" />
        <Tab label="لیست کاربران" />
      </Tabs>

      <TabContent activeTab={tab} index={0} loading={isFetching}>
        <Grid container alignItems="center">
          <Grid item xs={1}>
            <Avatar sx={{ width: 100, height: 100 }} />
          </Grid>
          <Grid item xs={11}>
            <Typography variant="h5" fontWeight={800} mb={3}>
              {`${data?.first_name || "-"} ${data?.last_name || ""}`}
            </Typography>

            <Grid container>
              <Grid item xs={3}>
                <Typography>مدیر</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography className="fa-number">موبایل: {data?.mobile || "-"}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography className="fa-number">کدملی: {data?.national_code || "-"}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography className="fa-number">تاریخ تولد: {data?.birth_date || "-"}</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Divider sx={{ my: 5 }} />

        <Typography variant="h5" fontWeight={800} mb={3}>
          ویرایش اطلاعات کاربری
        </Typography>

        <Grid container columnSpacing={2} rowSpacing={2}>
          {Inputs.map((input) => (
            <Grid item xs={12} md={2.5} key={input.name}>
              <TextField
                type={input.type}
                fullWidth
                label={input.label}
                name={input.name}
                value={profileData && profileData[input.name as keyof typeof profileData]}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleSetData(e, setProfileData)}
              />
            </Grid>
          ))}
          <Grid item xs={12} md={2.5}>
            <DatePicker
              fullWidth
              label="تاریخ تولد"
              name="birth_date"
              data={profileData}
              setData={(value) => setProfileData((prev) => ({ ...prev, birth_date: value }))}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <LoadingButton
              variant="contained"
              size="large"
              fullWidth
              onClick={() => updateProfileMutation.mutate()}
              loading={updateProfileMutation.isLoading}
            >
              ذخیره
            </LoadingButton>
          </Grid>
        </Grid>
      </TabContent>

      <TabContent activeTab={tab} index={1}>
        <Grid container columnSpacing={2} rowSpacing={2}>
          {Inputs.map((input) => (
            <Grid item xs={12} md={1.8} key={input.name}>
              <TextField
                type={input.type}
                fullWidth
                label={input.label}
                name={input.name}
                value={userFormData[input.name as keyof typeof userFormData]}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleSetData(e, setUserFormData)}
              />
            </Grid>
          ))}
          <Grid item xs={12} md={1.8}>
            <TextField
              label="موبایل"
              name="mobile"
              type="number"
              fullWidth
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleSetData(e, setUserFormData)}
            />
          </Grid>
          <Grid item xs={12} md={1.8}>
            <TextField
              label="دسترسی"
              fullWidth
              select
              name="role"
              value={userFormData.role}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleSetData(e, setUserFormData)}
            >
              <MenuItem value="admin">مدیر</MenuItem>
              <MenuItem value="user">اپراتور</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={1.2}>
            <LoadingButton
              loading={addUserMutation.isLoading}
              variant="contained"
              size="large"
              fullWidth
              onClick={() => addUserMutation.mutate()}
            >
              افزودن
            </LoadingButton>
          </Grid>
        </Grid>

        <Divider sx={{ my: 5 }} />

        <Table
          headerColumns={["ردیف", "نام ایستگاه", "نوع ایستگاه", "موقعیت مکانی", "عملیات"]}
          isLoading={false}
          data={[]}
          rowContent={null}
        />
      </TabContent>
    </>
  );
}
